/**
 * Tab «Carichi previsti» (M3) — il tabellone: avviso di ritiro → risposta
 * cliente → NDP → **partenza**. La riga resta qui finché la merce non esce,
 * come nel foglio Excel di Marina.
 *
 * Da agosto 2026 non è più solo osservazione: Marina segna il giorno di
 * partenza (serve NDP o DDT) e da lì la riga finisce in cima e sul tabellone
 * Partenze che guarda il presidente. Nessun documento fiscale, nessun invio.
 */

import {
  AlertTriangle,
  Bell,
  CalendarPlus,
  Check,
  ChevronDown,
  ChevronsUpDown,
  ChevronUp,
  Clock,
  Eye,
  File,
  FileCheck2,
  HelpCircle,
  Loader2,
  Mail,
  Pencil,
  Plus,
  Search,
  Truck,
  Upload,
  Users,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useCarichiPrevisti,
  useCpPartenza,
  useAnomalieTabellone,
  useCorreggiAnomalia,
  useCpRipristina,
  useCpPatch,
  useDuplicatiNdp,
  useIgnoraAnomalia,
  useFondiDuplicati,
  useM3DocumentiUpload,
} from "@/hooks/useM3";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { AvvioChat } from "@/components/m3/AidaSidebar";
import { CaricoPrevistoDrawer } from "@/components/m3/CaricoPrevistoDrawer";
import { PacchettoDrawer } from "@/components/m3/PacchettoDrawer";
import { NuovoCaricoDialog } from "@/components/m3/NuovoCaricoDialog";
import {
  cercaRighe,
  testoTarghe,
  eCongelato,
  eUscita,
  NDP_LABEL,
  STATO_LABEL,
  StatoBadge,
  eInPartenza,
} from "@/components/m3/stato-carico";
import { cn } from "@/lib/utils";
import type {
  AnomaliaTabellone,
  CaricoEliminato,
  CaricoPrevistoPatch,
  CaricoPrevistoRow,
  DocumentiUploadResponse,
  DuplicatoNdp,
  StatoCaricoPrevisto,
} from "@/types/api";

/**
 * I cinque riquadri della testata. Non sono KPI: sono le code di lavoro di
 * Marina, e ognuno è insieme il numero e il filtro che lo apre.
 *
 * Prima erano due fasce distinte — la striscia KPI che informava e i chip che
 * filtravano. Renderle entrambe cliccabili avrebbe creato due meccanismi di
 * filtro e la domanda «quale è attivo?»; fonderle toglie una fascia e aggiunge
 * funzioni. Fuori «Clienti in attesa» (informativo, quasi uguale a «Tutti») e il
 * **lead time medio**, che è un dato direzionale e va nel cruscotto M4.
 */
type Filtro = "attivi" | "da_sollecitare" | "con_domande" | "da_confermare_uscita" | "usciti";

const RIQUADRI: { id: Filtro; label: string; icona: typeof Users; urgente?: boolean }[] = [
  { id: "attivi", label: "Tutti", icona: Users },
  { id: "da_sollecitare", label: "Da sollecitare", icona: Bell, urgente: true },
  { id: "con_domande", label: "Con domande", icona: HelpCircle },
  { id: "da_confermare_uscita", label: "Da confermare uscita", icona: Truck },
  { id: "usciti", label: "Usciti", icona: Check },
];

/**
 * Bandiera dal codice ISO2 (lettere → regional indicator symbols).
 *
 * Le sigle dell'anagrafica sono codici targa, non ISO: la traduzione sta in
 * `m3.paese.iso2`, ricavata incrociando ogni sigla con la città dei clienti. Chi
 * non ha corrispondenza mostra un trattino — 30 righe su 121 non hanno il cliente
 * in anagrafica, e dirlo è meglio che mettere una bandiera indovinata.
 */
function Bandiera({ iso2, nome }: { iso2: string | null; nome: string | null }) {
  if (!iso2) {
    return (
      <span
        className="w-[19px] shrink-0 text-center text-[11px] font-semibold text-muted-foreground/60"
        title="Paese non risolto"
      >
        —
      </span>
    );
  }
  const emoji = [...iso2.toUpperCase()]
    .map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65))
    .join("");
  return (
    <span className="shrink-0 text-[17px] leading-none" title={nome ?? iso2}>
      {emoji}
    </span>
  );
}

/**
 * Colonna «Attenzione»: un solo segnale per riga, il più urgente.
 *
 * Sono tre cose diverse — *mi manca un dato tuo* / *non so di chi è* / *non hai
 * validato la mia lettura* — ma mostrarle tutte insieme produce solo «c'è
 * qualcosa che non va». Una colonna, una gerarchia, un posto dove guardare.
 */
function Attenzione({ row }: { row: CaricoPrevistoRow }) {
  if (row.domanda) {
    const campi = row.domanda.campi.map((c) => c.label).join(", ");
    return (
      <span
        title={`Aida ha chiesto: ${campi || row.domanda.testo || "un dato"} — da ${row.domanda.giorni_aperta} gg`}
        className="inline-flex items-center gap-0.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[11px] font-bold text-amber-800"
      >
        <HelpCircle className="h-3.5 w-3.5" />
        {row.domanda.campi.length > 1 && row.domanda.campi.length}
      </span>
    );
  }
  // La mail di avviso preparata dal portale che Aida non ha ancora visto passare
  // (01-10-2026): o non è partita, o è partita senza Aida in copia.
  if (row.mail_non_vista) {
    const il = row.avviso_preparato_il ? new Date(row.avviso_preparato_il).toLocaleDateString("it-IT") : "";
    return (
      <span
        title={`Mail preparata il ${il}: Aida non l'ha ancora vista. Mandala con Aida in copia.`}
        data-testid="mail-non-vista"
      >
        <Mail className="h-4 w-4 text-brand-600" />
      </span>
    );
  }
  if (row.match_incerto) {
    return (
      <span title="Cliente incerto: non sappiamo di chi è questa riga">
        <AlertTriangle className="h-4 w-4 text-amber-500" />
      </span>
    );
  }
  if (row.da_verificare) {
    return (
      <span title="Da verificare: Aida ha letto, nessuno ha confermato">
        <Eye className="h-4 w-4 text-muted-foreground" />
      </span>
    );
  }
  return <span className="text-muted-foreground">·</span>;
}

/**
 * Cella compilabile direttamente in tabella — l'approccio «Excel» chiesto da
 * Enrico, ma mirato: solo i campi che Aida può chiedere, e senza trasformare
 * ogni cella in un input (una tabella tutta di input non si legge più).
 *
 * Tre stati visivi: **accesa** se il dato manca ed è ciò che Aida sta aspettando,
 * tratteggiata se manca e nessuno l'ha chiesto, testo normale se c'è. Il click
 * apre l'editing in loco e non apre il drawer.
 *
 * Salvataggio automatico all'uscita dal campo, ma **dichiarato**: «salvato» che
 * compare e sfuma, rosso se fallisce. Senza il segnale non si capisce di aver
 * fatto qualcosa — è il difetto che la prova ha trovato.
 */
function CellaCampo({
  rowId,
  chiave,
  valore,
  tipo,
  atteso,
  fonte,
  pronto = false,
  inPiu,
}: {
  rowId: string;
  chiave: "data_ritiro" | "pronto_dal" | "pallet";
  /** Solo per i pallet: gli articoli di cui l'anagrafica non sa i pezzi per
   *  pallet. Il numero vale «più questi» (01-10-2026). */
  inPiu?: string[];
  valore: string | null;
  tipo: "date" | "number";
  atteso: boolean;
  /** Solo per `pronto_dal`: la merce è pronta ma la data non la sappiamo. È il
   *  caso delle righe che vengono dal foglio di Marina, dove «pronto» è una
   *  parola e non una data. */
  pronto?: boolean;
  /** Provenienza del valore: si mostra solo se NON è di Marina — un dato che ha
   *  scritto lei non ha bisogno di etichetta, uno messo da altri sì. */
  fonte?: string;
}) {
  const patch = useCpPatch(rowId);
  const [modifica, setModifica] = useState(false);
  const [bozza, setBozza] = useState(valore ?? "");
  const [salvato, setSalvato] = useState(false);

  useEffect(() => setBozza(valore ?? ""), [valore]);
  useEffect(() => {
    if (!salvato) return;
    const t = setTimeout(() => setSalvato(false), 1800);
    return () => clearTimeout(t);
  }, [salvato]);

  function salva(v: string | null) {
    setModifica(false);
    if ((v ?? "") === (valore ?? "")) return;
    const payload: CaricoPrevistoPatch =
      v === null || v === ""
        ? { [chiave]: null }
        : { [chiave]: tipo === "number" ? Number(v) : v };
    patch.mutate(payload, { onSuccess: () => setSalvato(true) });
  }

  const stop = (e: React.SyntheticEvent) => e.stopPropagation();

  if (modifica) {
    return (
      <td className="bg-brand-50/60 px-2.5 py-2" onClick={stop}>
        <input
          type={tipo}
          autoFocus
          step={tipo === "number" ? "1" : undefined}
          min={tipo === "number" ? "0" : undefined}
          value={bozza}
          onChange={(e) => setBozza(e.target.value)}
          onBlur={() => salva(bozza)}
          onKeyDown={(e) => {
            if (e.key === "Enter") salva(bozza);
            if (e.key === "Escape") {
              setBozza(valore ?? "");
              setModifica(false);
            }
          }}
          className="h-7 w-full min-w-[7rem] rounded border border-brand-400 bg-card px-1.5 text-xs"
        />
      </td>
    );
  }

  const mostrato =
    valore == null || valore === ""
      ? null
      : tipo === "date"
        ? new Date(valore).toLocaleDateString("it-IT")
        : Math.round(Number(valore));

  const etichettaFonte =
    fonte && fonte !== "marina" ? { ndp: "NDP", aida: "Aida", m1: "sistema" }[fonte] : null;

  // Oggi in ISO locale (YYYY-MM-DD). Si confronta con `valore` **come stringa**:
  // `new Date("2026-07-24")` è mezzanotte UTC, e in un fuso negativo tornerebbe
  // il giorno prima. Sulle date non c'è ora, quindi il confronto testuale è
  // l'unico che non può sbagliare di un giorno.
  const oggi = new Date().toLocaleDateString("sv-SE");

  // «Pronto dal» senza data: due situazioni che prima erano indistinguibili da
  // una cella vuota qualsiasi.
  //
  //  - non pronto  → un pulsante che TIMBRA il giorno in cui viene premuto
  //    (richiesta di Enrico: segnare pronto senza doversi ricordare la data,
  //    che poi resta modificabile come qualsiasi altra);
  //  - pronto senza data → un badge «Pronto» che dice il fatto. Non mostra una
  //    data perché non ce l'ha: quelle righe arrivano dal foglio, dove Marina
  //    scrive «pronto» e non da quando. Nessun conteggio di giorni le tocca.
  if (chiave === "pronto_dal" && mostrato == null) {
    return (
      <td className="group bg-brand-50/60 px-2.5 py-2" onClick={stop}>
        <span className="flex items-center gap-1">
          {pronto ? (
            <button
              type="button"
              onClick={() => setModifica(true)}
              disabled={patch.isPending}
              title="Pronto, data non indicata — clicca per aggiungerla"
              className={cn(
                "rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5",
                "text-[11px] font-semibold text-emerald-800 hover:bg-emerald-100",
                patch.isPending && "opacity-50",
              )}
            >
              Pronto
            </button>
          ) : (
            <button
              type="button"
              onClick={() => salva(oggi)}
              disabled={patch.isPending}
              title="Segna che la merce è pronta: registra la data di oggi"
              className={cn(
                "rounded px-1.5 py-0.5 text-xs ring-1 ring-inset transition-colors",
                atteso
                  ? "bg-amber-100 font-medium text-amber-900 ring-amber-300 hover:bg-amber-200"
                  : "text-muted-foreground/80 ring-border hover:bg-emerald-50 hover:text-emerald-800",
                patch.isPending && "opacity-50",
              )}
            >
              Pronto
            </button>
          )}
          <button
            type="button"
            onClick={() => setModifica(true)}
            title="Pronto da una data precisa"
            className="opacity-0 transition-opacity group-hover:opacity-100"
          >
            <CalendarPlus className="h-3 w-3 text-muted-foreground hover:text-brand-700" />
          </button>
          {pronto && (
            <button
              type="button"
              onClick={() => patch.mutate({ pronto: false })}
              title="Non è più pronta"
              className="opacity-0 transition-opacity group-hover:opacity-100"
            >
              <X className="h-3 w-3 text-muted-foreground hover:text-red-600" />
            </button>
          )}
          {salvato && <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" />}
          {patch.isError && (
            <span title="Salvataggio non riuscito">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-red-600" />
            </span>
          )}
        </span>
      </td>
    );
  }

  // «Pronto dal» con una data GIÀ ARRIVATA: la merce c'è, e il verde lo dice
  // scorrendo la colonna, senza leggere 76 date una per una. Dentro il badge
  // resta **la data**, non la parola: «pronta dal 24/07» a fine agosto vuol dire
  // ferma da cinque settimane, ed è il segnale più utile della colonna. Deciso
  // con Enrico (29-08) contro l'ipotesi del tooltip: questa pagina si stampa e
  // si scorre, e un dato che vive dietro il puntatore lì non esiste.
  //
  // ⚠️ Il confronto si fa QUI, non sul flag `pronto` del DB. Il backend lo
  // accende quando si scrive una `pronto_dal` già passata (campi_tabellone.py),
  // ma **nessuno lo rivaluta dopo**: una data futura scritta oggi resterebbe
  // `pronto=false` anche il giorno dopo che è arrivata. Il flag serve solo alle
  // righe che una data non ce l'hanno; dove la data c'è, comanda la data — ed è
  // anche il motivo per cui le due righe che oggi si contraddicono
  // (`pronto=true` con data futura) qui si vedono giuste.
  if (chiave === "pronto_dal" && valore && valore.slice(0, 10) <= oggi) {
    const iso = valore.slice(0, 10);
    const d = new Date(`${iso}T00:00:00`);
    // L'anno si mostra solo se non è quello in corso: in tabella «24/07» basta,
    // ma tenerlo quando serve non è pedanteria — ci sono righe con `pronto_dal`
    // nel 2027 e nel 2028 (anno digitato male), e senza anno sembrerebbero
    // date di quest'anno.
    const etichetta = d.toLocaleDateString(
      "it-IT",
      d.getFullYear() === new Date().getFullYear()
        ? { day: "2-digit", month: "2-digit" }
        : { day: "2-digit", month: "2-digit", year: "numeric" },
    );
    const giorni = Math.round((Date.parse(oggi) - Date.parse(iso)) / 86_400_000);
    return (
      <td className="group bg-brand-50/60 px-2.5 py-2" onClick={stop}>
        <span className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setModifica(true)}
            disabled={patch.isPending}
            title={`Pronta dal ${d.toLocaleDateString("it-IT")}${
              giorni === 0 ? " (oggi)" : giorni === 1 ? " (da ieri)" : ` (da ${giorni} giorni)`
            } — clicca per modificare`}
            className={cn(
              "rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5",
              "text-[11px] font-semibold tabular-nums text-emerald-800 hover:bg-emerald-100",
              patch.isPending && "opacity-50",
            )}
          >
            {etichetta}
          </button>
          {etichettaFonte && (
            <span className="shrink-0 text-[9.5px] text-muted-foreground" title={`Valore da ${etichettaFonte}`}>
              {etichettaFonte}
            </span>
          )}
          <button
            type="button"
            onClick={() => salva(null)}
            title="Svuota il campo"
            className="opacity-0 transition-opacity group-hover:opacity-100"
          >
            <X className="h-3 w-3 text-muted-foreground hover:text-red-600" />
          </button>
          {salvato && <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" />}
          {patch.isError && (
            <span title="Salvataggio non riuscito">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-red-600" />
            </span>
          )}
        </span>
      </td>
    );
  }

  return (
    <td className="group bg-brand-50/60 px-2.5 py-2" onClick={stop}>
      <span className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => setModifica(true)}
          disabled={patch.isPending}
          title={mostrato == null ? "Clicca per compilare" : "Clicca per modificare"}
          className={cn(
            "rounded px-1.5 py-0.5 text-left text-xs transition-colors",
            tipo === "number" && "tabular-nums",
            mostrato == null
              ? atteso
                ? "bg-amber-100 font-medium text-amber-900 ring-1 ring-inset ring-amber-300 hover:bg-amber-200"
                : "min-w-[3rem] text-muted-foreground/70 ring-1 ring-inset ring-border hover:bg-muted"
              : "hover:bg-muted",
            patch.isPending && "opacity-50",
          )}
        >
          {mostrato ?? (atteso ? "manca" : "—")}
        </button>
        {inPiu && inPiu.length > 0 && (
          <span
            className="shrink-0 rounded bg-amber-100 px-1 text-[9.5px] font-semibold text-amber-900"
            title={`Più: ${inPiu.join(", ")} — l'anagrafica non sa quanti pezzi stanno su un pallet`}
            data-testid="pallet-in-piu"
          >
            +{inPiu.length}
          </span>
        )}
        {etichettaFonte && (
          <span className="shrink-0 text-[9.5px] text-muted-foreground" title={`Valore da ${etichettaFonte}`}>
            {etichettaFonte}
          </span>
        )}
        {mostrato != null && (
          <button
            type="button"
            onClick={() => salva(null)}
            title="Svuota il campo"
            className="opacity-0 transition-opacity group-hover:opacity-100"
          >
            <X className="h-3 w-3 text-muted-foreground hover:text-red-600" />
          </button>
        )}
        {salvato && <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" />}
        {patch.isError && (
          <span title="Salvataggio non riuscito">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-red-600" />
          </span>
        )}
      </span>
    </td>
  );
}

/**
 * La nota, scrivibile in tabella.
 *
 * Le note del foglio di Marina arrivano qui: targhe, orari, «manca 1 pallet
 * msp7506». Sono brevi (26 caratteri di media, 76 la più lunga, nessuna su più
 * righe) e la colonna è stretta, quindi si leggono troncate col testo intero nel
 * tooltip, e si scrivono **in tabella** come gli altri campi della riga.
 *
 * Perché inline e non una finestra (deciso con Enrico, 27-08): nella stessa riga
 * tre celle si modificano già cliccandole, e una finestra per la quarta
 * darebbe due grammatiche alla stessa tabella. La nota si scrive **guardando la
 * riga** — «tra le 11 e le 13 -LJ 80-PLH» ha senso accanto a cliente, pallet e
 * ritiro — e una finestra coprirebbe proprio il contesto che serve. Chi deve
 * scrivere con calma ha già la textarea del drawer.
 *
 * Tre accortezze che le celle-data non richiedono:
 *  - in modifica il campo **si espande sopra la tabella** (la cella è 10rem, la
 *    nota arriva a 76 caratteri) e si ancora a destra, perché Note è l'ultima
 *    colonna e un pannello a sinistra uscirebbe dallo schermo;
 *  - il bersaglio è **tutta la cella**: la matita è il segnale, non il punto da
 *    centrare — e serve soprattutto sulle righe che mostrano «—», dove nulla
 *    diceva che si potesse scrivere;
 *  - `stopPropagation`, o il clic aprirebbe anche il drawer sopra il campo.
 */
function CellaNota({ rowId, nota }: { rowId: string; nota: string | null }) {
  const patch = useCpPatch(rowId);
  const [modifica, setModifica] = useState(false);
  const [bozza, setBozza] = useState(nota ?? "");
  const [salvato, setSalvato] = useState(false);

  useEffect(() => setBozza(nota ?? ""), [nota]);
  useEffect(() => {
    if (!salvato) return;
    const t = setTimeout(() => setSalvato(false), 1800);
    return () => clearTimeout(t);
  }, [salvato]);

  function salva(testo: string) {
    setModifica(false);
    if (testo.trim() === (nota ?? "").trim()) return;
    patch.mutate({ note: testo.trim() }, { onSuccess: () => setSalvato(true) });
  }

  const stop = (e: React.SyntheticEvent) => e.stopPropagation();

  return (
    <td className="group relative max-w-[10rem] px-2.5 py-2 text-xs text-muted-foreground" onClick={stop}>
      {modifica ? (
        // Ancorata a destra e sopra il resto: `absolute` invece di allargare la
        // cella, così la tabella non si scompone mentre si scrive.
        <div className="absolute right-2 top-1 z-20 w-[24rem] rounded-md border bg-card p-2 shadow-lg">
          <textarea
            autoFocus
            rows={3}
            value={bozza}
            onChange={(e) => setBozza(e.target.value)}
            onBlur={() => salva(bozza)}
            onKeyDown={(e) => {
              // Enter salva perché la nota sta su una riga (nessuna delle 25 note
              // del foglio va a capo); Shift+Enter resta per chi ne ha bisogno.
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                salva(bozza);
              }
              if (e.key === "Escape") {
                setBozza(nota ?? "");
                setModifica(false);
              }
            }}
            placeholder="Targhe, orario, «manca 1 pallet»…"
            className="w-full resize-none rounded border bg-card p-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <p className="mt-1 text-[10px] text-muted-foreground">
            Invio salva · Maiusc+Invio va a capo · Esc annulla
          </p>
        </div>
      ) : (
        <span className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setModifica(true)}
            disabled={patch.isPending}
            title={nota || "Clicca per scrivere una nota"}
            className={cn(
              "min-w-0 flex-1 truncate rounded px-1 py-0.5 text-left hover:bg-muted",
              patch.isPending && "opacity-50",
            )}
          >
            {nota || "—"}
          </button>
          <Pencil className="h-3 w-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-100" />
          {nota && (
            <button
              type="button"
              onClick={() => salva("")}
              title="Cancella la nota"
              className="shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
            >
              <X className="h-3 w-3 hover:text-red-600" />
            </button>
          )}
          {salvato && <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" />}
          {patch.isError && (
            <span title="Salvataggio non riuscito">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-red-600" />
            </span>
          )}
        </span>
      )}
    </td>
  );
}

/**
 * La colonna Tempo, **una sola** che cambia significato con la fase della riga
 * (Enrico, 06-10-2026: «colonna unica»):
 *
 * - **cliente** — prima della NDP: da quanto il cliente ha avvisato (vivo). È il
 *   tempo che misura la pazienza del cliente, rosso quando va sollecitato.
 * - **spedizione** — dalla NDP in poi: da quanto il carico è pronto da spedire
 *   (vivo, dall'ora di creazione della NDP in Cube). È l'efficienza del reparto
 *   spedizioni, rossa oltre la soglia delle Impostazioni.
 * - **spedita in** — con il DDT o l'uscita: lo stesso tempo, congelato.
 *
 * Le righe con NDP letta da Aida prima del DB non hanno l'ora della NDP: per
 * quelle resta il tempo cliente congelato, come prima.
 */
type Tempo = { giorni: number; fase: "cliente" | "spedizione" | "spedita"; dal: string };

function tempoRiga(row: CaricoPrevistoRow): Tempo | null {
  if (row.ndp_emessa_il) {
    const fine = row.ddt_emesso_il ?? row.partito_il;
    const giorni = ((fine ? new Date(fine).getTime() : Date.now()) - new Date(row.ndp_emessa_il).getTime()) / 86_400_000;
    // Un DDT stampato prima della NDP (succede: NDP rifatta) non è un'attesa negativa.
    return { giorni: Math.max(giorni, 0), fase: fine ? "spedita" : "spedizione", dal: row.ndp_emessa_il };
  }
  if (row.genesi === "ndp" || !row.avvisato_il) return null;
  const giorni =
    eCongelato(row.stato) && row.lead_time_giorni != null
      ? row.lead_time_giorni
      : (Date.now() - new Date(row.avvisato_il).getTime()) / 86_400_000;
  return { giorni, fase: "cliente", dal: row.avvisato_il };
}

function giorniTempo(row: CaricoPrevistoRow): number | null {
  return tempoRiga(row)?.giorni ?? null;
}

/** Il tempo della riga, che si aggiorna da solo finché è vivo. */
function useTempo(row: CaricoPrevistoRow): Tempo | null {
  const [, tick] = useState(0);
  const t = tempoRiga(row);
  const vivo = t != null && t.fase !== "spedita" && !(t.fase === "cliente" && eCongelato(row.stato));
  useEffect(() => {
    if (!vivo) return;
    const i = setInterval(() => tick((n) => n + 1), 60_000);
    return () => clearInterval(i);
  }, [vivo]);
  return t;
}

const FASE_LABEL: Record<Tempo["fase"], string> = {
  cliente: "cliente",
  spedizione: "da NDP",
  spedita: "spedita in",
};

function CellaTempo({ row, soglia }: { row: CaricoPrevistoRow; soglia: number }) {
  const t = useTempo(row);
  if (!t) {
    return <td className="px-2.5 py-2 tabular-nums text-muted-foreground">n/d</td>;
  }
  const dal = new Date(t.dal);
  const quando = `${dal.toLocaleDateString("it-IT")} ${dal.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}`;
  const title =
    t.fase === "cliente"
      ? `Avvisato il ${dal.toLocaleDateString("it-IT")} ${row.canale_avviso === "telefono" ? "(telefono)" : "(email)"}`
      : t.fase === "spedizione"
        ? `In attesa di spedizione dalla NDP del ${quando} · soglia ${soglia} gg (Impostazioni)`
        : `Dalla NDP (${quando}) all'uscita${row.ddt_numero ? ` col DDT ${row.ddt_numero}` : ""}`;
  const rosso =
    (t.fase === "cliente" && row.stato === "sollecito_dovuto") ||
    (t.fase === "spedizione" && t.giorni > soglia);
  return (
    <td className="px-2.5 py-2 tabular-nums" data-testid="tempo-riga" data-fase={t.fase}>
      <span title={title} className={cn("inline-flex flex-col leading-tight", rosso && "text-red-600")}>
        <span className={cn("inline-flex items-center gap-1", rosso && "font-semibold")}>
          {t.fase !== "spedita" && !(t.fase === "cliente" && eCongelato(row.stato)) && (
            <Clock className="h-3.5 w-3.5" />
          )}
          {t.giorni.toFixed(1)} gg
        </span>
        <span className={cn("text-[10px]", rosso ? "text-red-600/80" : "text-muted-foreground")}>
          {FASE_LABEL[t.fase]}
        </span>
      </span>
    </td>
  );
}

function Riga({
  row,
  soglia,
  onClick,
}: {
  row: CaricoPrevistoRow;
  soglia: number;
  onClick: () => void;
}) {
  // I campi che Aida sta aspettando su questa riga: sono quelli che si accendono.
  const attesi = new Set((row.domanda?.campi ?? []).map((c) => c.chiave));
  return (
    <tr
      // L'id sulla riga è l'ancoraggio delle prove in browser: cercarla per
      // nome cliente non funziona (44 righe contengono «Fidrik») e si finisce
      // per provare una riga diversa da quella che si crede.
      data-riga={row.id}
      className={cn(
        "cursor-pointer border-b hover:bg-muted/40",
        row.domanda && "bg-amber-50/40",
        eUscita(row.stato) && "text-muted-foreground",
      )}
      onClick={onClick}
    >
      <td className="px-2 py-2"><Attenzione row={row} /></td>

      {/* Cliente: è la chiave con cui Marina cerca la riga, quindi domina.
          Bandiera dalla sigla dell'anagrafica tradotta in ISO. */}
      <td className="max-w-[15rem] px-2.5 py-2">
        <span className="flex items-center gap-2">
          <Bandiera iso2={row.paese_iso2} nome={row.paese_nome} />
          {row.cliente_ignoto ? (
            <span className="min-w-0">
              <span className="rounded-full border border-dashed border-red-400 bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700">
                Cliente da identificare
              </span>
              {(row.trasportatore || letto(row)) && (
                <span className="mt-0.5 block truncate text-[10.5px] text-muted-foreground">
                  ritira {row.trasportatore ?? `${letto(row)}?`}
                </span>
              )}
            </span>
          ) : (
            <span className="min-w-0" title={row.cliente_nome}>
              <span
                className={cn(
                  "block truncate text-[13.5px] font-semibold leading-tight tracking-tight",
                  eUscita(row.stato) && "font-medium text-muted-foreground",
                )}
              >
                {row.cliente_nome}
              </span>
              {(row.paese_nome || row.intermediario || row.trasportatore || row.ritira_il_cliente || letto(row)) && (
                <span className="block truncate text-[10.5px] text-muted-foreground">
                  {row.paese_nome}
                  {row.intermediario && ` · via ${row.intermediario}`}
                  {/* Chi ritira, sotto il cliente e senza una colonna in più
                      (Enrico, 30-09: «per ogni carico sarà evidente chi è il
                      cliente e chi lo spedizioniere»). Si cambia dal drawer. */}
                  {row.ritira_il_cliente ? (
                    <span className="font-medium text-foreground/70"> · ritira il cliente</span>
                  ) : row.trasportatore ? (
                    <span className="font-medium text-foreground/70"> · ritira {row.trasportatore}</span>
                  ) : letto(row) ? (
                    <span className="italic" title="Spedizioniere letto nella mail: da confermare nel drawer">
                      {" "}· ritira {letto(row)}?
                    </span>
                  ) : null}
                </span>
              )}
            </span>
          )}
        </span>
      </td>

      {/* Commessa + riferimenti: erano due colonne, ma sono la stessa cosa a due
          livelli di certezza — la commessa nostra e come la chiama il cliente. */}
      <td className="px-2.5 py-2">
        <span className="font-semibold tabular-nums">
          {row.commesse.length ? (
            // Una commessa presa a stralcio si segna: nel foglio di Marina è
            // «805+ STRALCI 774», e senza il segno le due righe sembrerebbero
            // due carichi interi dello stesso ordine.
            row.commesse.map((c, i) => {
              const info = row.commesse_info?.find((x) => x.numero === c);
              return (
              <span key={c}>
                {i > 0 && ", "}
                <span
                  className={cn(
                    info && !info.esiste && "text-amber-700 underline decoration-dotted",
                    info && info.esiste && !info.aperta && "font-medium text-muted-foreground",
                  )}
                  title={
                    info && !info.esiste
                      ? "Non risulta fra le commesse: forse è il numero d'ordine del cliente"
                      : info && !info.aperta
                        ? "Chiusa: non è più nell'export ordini (già spedita?)"
                        : info?.di_altro_cliente
                          ? `Commessa di ${info.di_altro_cliente}`
                          : undefined
                  }
                >
                  {c}
                </span>
                {info?.di_altro_cliente && (
                  <AlertTriangle className="ml-0.5 inline h-3 w-3 align-[-2px] text-amber-600" aria-label={`di ${info.di_altro_cliente}`} />
                )}
                {row.commesse_parziali?.includes(c) && (
                  <span
                    className="ml-0.5 align-super text-[9px] font-medium text-muted-foreground"
                    title="Il carico prende questa commessa solo in parte (stralcio)"
                  >
                    str.
                  </span>
                )}
              </span>
              );
            })
          ) : (
            <span className="font-normal text-muted-foreground">—</span>
          )}
        </span>
        {/* Come chiama la commessa il cliente, dall'export ordini: è il numero
            che scrive nelle mail (30-09). Se non c'è, i riferimenti citati. */}
        {(() => {
          const rif = (row.commesse_info ?? [])
            .map((c) => c.riferimento_cliente)
            .filter((x): x is string => !!x);
          const testo = rif.length ? rif.join(" · ") : row.riferimenti_cliente.join(" · ");
          return testo ? (
            <span
              className="block max-w-[10rem] truncate text-[10.5px] text-muted-foreground"
              title={rif.length ? `Per il cliente: ${testo}` : `Citati nelle mail: ${testo}`}
            >
              {rif.length ? <>rif. {testo}</> : testo}
            </span>
          ) : null;
        })()}
      </td>

      <td className="px-2.5 py-2">
        <StatoBadge stato={row.stato} inPartenza={eInPartenza(row)} />
      </td>

      {/* I tre campi compilabili, su fondo proprio: è l'unica zona in cui si
          SCRIVE, e raggrupparla la rende riconoscibile senza dover trasformare
          ogni cella della tabella in un input. */}
      <CellaCampo
        rowId={row.id}
        chiave="data_ritiro"
        valore={row.data_ritiro}
        tipo="date"
        atteso={attesi.has("data_ritiro")}
        fonte={row.campi_fonte?.data_ritiro?.fonte}
      />
      <CellaCampo
        rowId={row.id}
        chiave="pronto_dal"
        valore={row.pronto_dal}
        tipo="date"
        atteso={attesi.has("pronto_dal")}
        fonte={row.campi_fonte?.pronto_dal?.fonte}
        pronto={row.pronto}
      />
      <CellaCampo
        rowId={row.id}
        chiave="pallet"
        valore={row.pallet_previsti != null ? String(row.pallet_previsti) : null}
        tipo="number"
        atteso={attesi.has("pallet")}
        fonte={row.campi_fonte?.pallet?.fonte}
        inPiu={row.pallet_senza_dato}
      />

      {/* NDP: il numero, non «Emessa». Quello lo diceva già il badge di stato,
          mentre il numero serve quando si cerca un documento e non si vedeva. */}
      <td className="px-2.5 py-2">
        {row.ndp_numero ? (
          <span className="inline-flex items-center gap-1.5 tabular-nums" title="NDP emessa">
            <FileCheck2 className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
            {row.ndp_numero}
          </span>
        ) : (
          <span title={NDP_LABEL[row.ndp_badge] ?? row.ndp_badge}>
            <File className="h-3.5 w-3.5 text-muted-foreground/50" />
          </span>
        )}
      </td>

      {/* DDT: la seconda colonna dei documenti (06-10-2026, «così vediamo
          anche quali hanno il DDT emesso e quali no»). Sul tabellone un DDT
          c'è solo se è di oggi: quelli dei giorni prima hanno già fatto partire
          la riga. */}
      <td className="px-2.5 py-2" data-testid="ddt-riga">
        {row.ddt_numero ? (
          <span
            className="inline-flex items-center gap-1.5 tabular-nums"
            title={
              row.ddt_emesso_il
                ? `DDT stampato il ${new Date(row.ddt_emesso_il).toLocaleString("it-IT", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}`
                : row.ddt_data
                  ? `DDT del ${new Date(row.ddt_data).toLocaleDateString("it-IT")}`
                  : "DDT emesso"
            }
          >
            <FileCheck2 className="h-3.5 w-3.5 shrink-0 text-amber-600" />
            {row.ddt_numero}
          </span>
        ) : (
          <span className="text-muted-foreground/50">—</span>
        )}
      </td>

      <CellaPartenza row={row} />

      <CellaTempo row={row} soglia={soglia} />
      <CellaNota rowId={row.id} nota={row.note} />
    </tr>
  );
}

/**
 * Colonna «Partenza» — l'uscita è un FATTO che si registra, non un permesso che
 * si chiede.
 *
 * Prima l'azione stava nel drawer e le parole («segna il giorno di partenza»,
 * «confermo l'uscita») facevano sembrare che il sistema autorizzasse la merce a
 * partire. Ora: un click sulla riga, «è uscito», adesso. Il giorno previsto resta
 * solo per pianificare in avanti, dal drawer.
 *
 * Il comando compare **appena c'è la NDP**: Enrico ha portato la regola di
 * dominio che mi mancava — la nota di prelievo si emette poco prima che il camion
 * esca, quindi è già il segnale che la partenza è imminente.
 *
 * La strada definitiva resta la riconciliazione col DDT — il documento che
 * accompagna la merce che esce è già la prova che è partita, e porta anche la data
 * giusta. Il backend è predisposto (`riconcilia_uscita_da_ddt`); questo pulsante
 * serve per quando il DDT arriva dopo, o non arriva.
 */
function CellaPartenza({ row }: { row: CaricoPrevistoRow }) {
  const partenza = useCpPartenza(row.id);
  const oggi = new Date().toISOString().slice(0, 10);

  if (row.partito_il) {
    // ⚠️ Uscita **dichiarata** senza documenti: si vede qui e non solo nel
    // drawer, altrimenti «Usciti» conterrebbe due popolazioni indistinguibili —
    // spedizioni documentate e spedizioni prese sulla parola.
    const dichiarata = row.uscita_forzata_motivo != null;
    return (
      <td className="px-2.5 py-2">
        <span className="flex items-center gap-1">
          <span
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700"
            title={`Uscita il ${new Date(row.partito_il).toLocaleDateString("it-IT")}`}
          >
            <Truck className="h-3.5 w-3.5" />
            Uscito
          </span>
          {dichiarata && (
            <span
              className="rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[9.5px] font-semibold text-amber-900"
              title={
                (row.uscita_forzata_motivo === "evasa_in_cube"
                  ? "NDP evasa in Cube senza DDT"
                  : row.uscita_forzata_motivo === "ndp_non_caricata"
                    ? "NDP emessa ma non caricata nel portale"
                    : "Spedizione senza NDP (carico a mano)") +
                (row.uscita_forzata_nota ? ` · ${row.uscita_forzata_nota}` : "")
              }
            >
              {row.uscita_forzata_motivo === "evasa_in_cube" ? "senza DDT" : "senza NDP"}
            </span>
          )}
        </span>
        <TargheRiga row={row} />
      </td>
    );
  }

  if (!row.puo_confermare_uscita) {
    // Manca la NDP (o il DDT): senza documento non c'è niente da confermare.
    return (
      <td className="px-2.5 py-2">
        <span
          className="text-xs text-muted-foreground"
          title="Serve la NDP (o il DDT)"
        >
          {row.data_partenza_prevista
            ? new Date(row.data_partenza_prevista).toLocaleDateString("it-IT")
            : "—"}
        </span>
        <TargheRiga row={row} />
      </td>
    );
  }

  // Con il DDT (06-10-2026) l'uscita si registra da sola: un DDT dei giorni
  // prima fa partire la riga, uno di oggi la lascia qui «in partenza» fino a
  // domani. Il camion resta per chi vuole chiuderla subito, e per le NDP che il
  // DDT non l'avranno: piccolo, un'icona, non più il pulsante pieno che chiedeva
  // a Marina un gesto su ogni riga.
  const inPartenza = eInPartenza(row);
  const previstoOggi = row.data_partenza_prevista === oggi;
  const titolo = inPartenza
    ? `DDT ${row.ddt_numero} emesso oggi: domani la riga parte da sola. Clicca se il camion è già uscito.`
    : "Conferma che il camion è uscito (oggi)";
  return (
    <td className="px-2.5 py-2" onClick={(e) => e.stopPropagation()}>
      <span className="flex items-center gap-1.5">
        {inPartenza ? (
          <span className="text-[11px] font-medium text-amber-800">in partenza</span>
        ) : (
          row.data_partenza_prevista && (
            <span className="text-[11px] text-muted-foreground">
              {previstoOggi ? "oggi" : new Date(row.data_partenza_prevista).toLocaleDateString("it-IT")}
            </span>
          )
        )}
        <button
          type="button"
          disabled={partenza.isPending}
          onClick={() => partenza.mutate({ uscito: true })}
          title={titolo}
          aria-label="Conferma uscita"
          data-testid="conferma-uscita"
          className={cn(
            "inline-flex h-6 w-6 items-center justify-center rounded-md border transition-colors disabled:opacity-50",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            inPartenza || previstoOggi
              ? "border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100"
              : "border-border text-muted-foreground hover:bg-muted hover:text-foreground",
          )}
        >
          {partenza.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Truck className={cn("h-3.5 w-3.5", inPartenza && "camion-in-viaggio")} />
          )}
        </button>
      </span>
      <TargheRiga row={row} />
    </td>
  );
}

/** La targa del mezzo sotto l'uscita: è il camion che esce (01-10-2026,
 *  «le targhe sono importanti»). Si cambia dal drawer, carta «Chi ritira». */
function TargheRiga({ row }: { row: CaricoPrevistoRow }) {
  const testo = testoTarghe(row);
  if (!testo) return null;
  const fonte = row.campi_fonte?.targa_motrice?.fonte ?? row.campi_fonte?.targa_rimorchio?.fonte;
  return (
    <span
      className="mt-0.5 block max-w-[9rem] truncate font-mono text-[10.5px] tracking-tight text-muted-foreground"
      title={`Targa ${testo}${fonte === "aida" ? " · letta da Aida in una mail" : ""}`}
      data-testid="targa-riga"
    >
      {testo}
    </span>
  );
}

// ── Ordinamento e ricerca (client-side) ──────────────────────────────
//
// Client-side perché le righe arrivano in un solo payload: l'ordinamento è
// istantaneo e il backend non si tocca.
// ⚠️ Regge finché il tabellone sta in una risposta sola. Oltre il migliaio di
// righe va spostato sul server, insieme alla ricerca.

/** Solo le colonne che hanno un ordine sensato: un'intestazione che sembra
 *  cliccabile ma non lo è è peggio di una normale. Fuori Attenzione, Uscita, Note. */
type Colonna =
  | "cliente"
  | "commessa"
  | "stato"
  | "data_ritiro"
  | "pronto_dal"
  | "pallet"
  | "ndp"
  | "ddt"
  | "tempo";

type Ordine = { col: Colonna; desc: boolean } | null;

/** Ordine di ciclo di vita, non alfabetico: «Sollecitare» in cima vale più di
 *  «Allineato DDT» prima di «Confermato». */
const ORDINE_STATO: Record<StatoCaricoPrevisto, number> = {
  sollecito_dovuto: 0,
  in_attesa: 1,
  interlocutoria: 2,
  posticipato: 3,
  confermato: 4,
  ndp_emessa: 5,
  allineato_ddt: 6,
  partito: 7,
};

function valoreCol(row: CaricoPrevistoRow, col: Colonna): string | number | null {
  switch (col) {
    case "cliente":
      return row.cliente_nome?.toLowerCase() ?? null;
    case "commessa":
      return row.commesse.length ? row.commesse[0] : null;
    case "stato":
      return ORDINE_STATO[row.stato];
    case "data_ritiro":
      return row.data_ritiro;
    case "pronto_dal":
      return row.pronto_dal;
    case "pallet":
      return row.pallet_previsti;
    case "ndp":
      return row.ndp_numero?.toLowerCase() ?? null;
    case "ddt":
      return row.ddt_numero?.toLowerCase() ?? null;
    case "tempo":
      return giorniTempo(row);
  }
}

function ordina(items: CaricoPrevistoRow[], ord: Ordine): CaricoPrevistoRow[] {
  if (!ord) return items;
  const segno = ord.desc ? -1 : 1;
  return [...items].sort((a, b) => {
    const va = valoreCol(a, ord.col);
    const vb = valoreCol(b, ord.col);
    // I vuoti restano in fondo in entrambi i versi: una colonna di trattini in
    // cima non è un ordinamento, è rumore.
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    return va < vb ? -segno : va > vb ? segno : 0;
  });
}

/**
 * Cerca su nome cliente · commessa · NDP · **riferimenti cliente** · note.
 *
 * I riferimenti sono la parte che serve davvero: Marina cerca quello che scrive
 * il cliente — `26IT0992`, `ORDER NO 742`, `PO 18` — e senza quelli la ricerca
 * fallisce proprio nei casi in cui la si usa.
 */
function IntestazioneOrd({
  col,
  label,
  ord,
  onOrd,
  className,
}: {
  col: Colonna;
  label: string;
  ord: Ordine;
  onOrd: (c: Colonna) => void;
  className?: string;
}) {
  const attiva = ord?.col === col;
  return (
    <th className={cn("px-2.5 py-2 font-medium", className)}>
      <button
        type="button"
        onClick={() => onOrd(col)}
        className="inline-flex items-center gap-1 hover:text-foreground"
        title={`Ordina per ${label.toLowerCase()}`}
      >
        {label}
        {attiva ? (
          ord.desc ? (
            <ChevronDown className="h-3 w-3" />
          ) : (
            <ChevronUp className="h-3 w-3" />
          )
        ) : (
          <ChevronsUpDown className="h-3 w-3 opacity-30" />
        )}
      </button>
    </th>
  );
}

/** Un riquadro della testata: il numero È il filtro. */
function Riquadro({
  icona: Icona,
  label,
  valore,
  attivo,
  urgente,
  onClick,
}: {
  icona: typeof Users;
  label: string;
  valore: number | undefined;
  attivo: boolean;
  urgente?: boolean;
  onClick: () => void;
}) {
  const vuoto = valore === 0;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={attivo}
      className={cn(
        "rounded-lg border p-3 text-left transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        attivo
          ? "border-brand-700 bg-brand-50 ring-1 ring-brand-700"
          : "bg-card hover:border-brand-400 hover:bg-brand-50/50",
        // Una coda vuota non è un errore: si spegne invece di sparire, così la
        // testata non cambia forma sotto le mani di Marina.
        vuoto && !attivo && "opacity-60",
      )}
    >
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Icona className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{label}</span>
      </div>
      <div
        className={cn(
          "mt-1 text-2xl font-bold tabular-nums",
          urgente && !vuoto && "text-red-600",
        )}
      >
        {valore ?? "—"}
      </div>
    </button>
  );
}

/**
 * Il riquadro di rilascio: NDP, DDT e fattura si caricano da qui, senza cambiare
 * tab.
 *
 * Riquadro **visibile** e non bersaglio invisibile su tutta l'area (scelta di
 * Enrico): un'area di drop che non si vede non si usa, e chi non trascina non ha
 * modo di scoprirla. Riusa lo **stesso endpoint** della sezione Documenti —
 * due pipeline divergerebbero al primo bug.
 */
function RiquadroRilascio({
  onRiga,
  onDocumenti,
}: {
  /** Porta alla riga del tabellone che il documento ha cambiato. */
  onRiga: (caricoId: string) => void;
  /** Manda alla sezione Documenti, dove c'è la coda «assegna a carico». */
  onDocumenti: () => void;
}) {
  const upload = useM3DocumentiUpload();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [esito, setEsito] = useState<DocumentiUploadResponse | null>(null);
  const [errore, setErrore] = useState<string | null>(null);

  function invia(file: File) {
    setEsito(null);
    setErrore(null);
    upload.mutate(
      { file },
      {
        onSuccess: setEsito,
        onError: (e: unknown) =>
          setErrore(e instanceof Error ? e.message : "Caricamento non riuscito."),
      },
    );
  }

  const caricoEsito = esito?.pacco?.carico_id ?? null;
  return (
    <div className="w-full sm:w-[26rem]">
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) invia(file);
          e.target.value = "";
        }}
      />
      <div
        role="button"
        tabIndex={0}
        onClick={() => !upload.isPending && inputRef.current?.click()}
        onKeyDown={(e) => e.key === "Enter" && inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const file = e.dataTransfer.files?.[0];
          if (file) invia(file);
        }}
        className={cn(
          "flex h-[38px] cursor-pointer items-center gap-2 rounded-md border-[1.5px] border-dashed px-3 transition-colors",
          dragOver
            ? "border-brand-600 bg-brand-50"
            : "border-border hover:border-brand-400 hover:bg-brand-50/50",
        )}
      >
        {upload.isPending ? (
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-brand-700" />
        ) : (
          <Upload className="h-4 w-4 shrink-0 text-brand-700" />
        )}
        <span className="truncate text-xs text-muted-foreground">
          {upload.isPending
            ? "Aida sta leggendo il documento…"
            : "Rilascia qui NDP, DDT o fattura"}
        </span>
      </div>

      {/* L'esito: cosa è cambiato, e la strada per andarci. Un documento che
          sparisce senza dire niente è il modo più veloce per perdere fiducia. */}
      {(esito || errore) && (
        <div
          className={cn(
            "mt-1.5 flex items-start gap-1.5 rounded-md px-2 py-1.5 text-[11px]",
            errore || esito?.esito === "non_riconosciuto"
              ? "bg-red-50 text-red-700"
              : esito?.esito === "in_coda_match"
                ? "bg-amber-50 text-amber-800"
                : "bg-emerald-50 text-emerald-800",
          )}
        >
          <span className="min-w-0 flex-1">
            {errore ?? esito?.messaggio}
            {caricoEsito && (
              <button
                type="button"
                onClick={() => onRiga(caricoEsito)}
                className="ml-1 font-semibold underline underline-offset-2"
              >
                Vai alla riga
              </button>
            )}
            {esito?.esito === "in_coda_match" && (
              <button
                type="button"
                onClick={onDocumenti}
                className="ml-1 font-semibold underline underline-offset-2"
              >
                Assegnalo dalla sezione Documenti
              </button>
            )}
          </span>
          <button
            type="button"
            onClick={() => {
              setEsito(null);
              setErrore(null);
            }}
            className="shrink-0 rounded p-0.5 hover:bg-black/5"
            aria-label="Chiudi"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Il riquadro delle NDP duplicate: lo dice, e propone di unirle.
 *
 * Fino al 12-09-2026 una NDP corretta e rimandata creava una riga nuova invece
 * di aggiornare quella che quel numero ce l'aveva già — la 1131 quattro volte.
 * Il difetto è chiuso, ma le copie restano sul tabellone, e Marina si trova una
 * riga piena dei suoi dati accanto a una gemella vuota.
 *
 * Compare **solo se ce n'è**: è una condizione in via di estinzione, e un
 * riquadro fisso a zero sarebbe rumore permanente per un problema temporaneo.
 *
 * ⚠️ I gruppi **sospetti** (nessuna riga nata da NDP) si fondono lo stesso, ma
 * lo dicono prima: possono essere due carichi veri marcati con lo stesso numero
 * — la 1137 tiene insieme 1 pallet di luglio e 33 di agosto. Quella scelta è di
 * Marina, non nostra, ed è per questo che il pulsante c'è ma avvisa.
 */
function RiquadroDuplicati() {
  const { data } = useDuplicatiNdp();
  const fondi = useFondiDuplicati();
  const [conferma, setConferma] = useState<DuplicatoNdp | null>(null);
  const [esito, setEsito] = useState<string | null>(null);
  const gruppi = data ?? [];

  if (!gruppi.length) return esito ? <p className="text-xs text-emerald-700">{esito}</p> : null;

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50/60 p-3">
      <div className="flex flex-wrap items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-amber-900">
            {gruppi.length === 1
              ? "Una NDP compare su più righe"
              : `${gruppi.length} NDP compaiono su più righe`}
          </p>
          <p className="mt-0.5 text-xs text-amber-800">
            Succede quando la stessa nota viene corretta e rimandata. Unendole resta una riga
            sola: i dati del documento li porta la NDP più recente, ritiro, note e «pronto dal»
            restano quelli che hai messo tu.
          </p>
          <ul className="mt-2 space-y-1">
            {gruppi.map((g) => (
              <li key={g.ndp_numero} className="flex flex-wrap items-center gap-2 text-xs">
                <span className="font-medium text-amber-900">NDP {g.ndp_numero}</span>
                <span className="text-amber-800">· {g.cliente_nome}</span>
                <span className="text-amber-700">· {g.righe.length} righe</span>
                {g.sospetto && (
                  <span className="rounded-full bg-amber-200 px-1.5 py-0.5 text-[10px] font-medium text-amber-900">
                    da guardare: nessuna nasce da NDP
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => setConferma(g)}
                  className="rounded-md border border-amber-400 bg-card px-2 py-0.5 font-medium hover:bg-amber-100"
                >
                  Unisci
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
      {esito && <p className="mt-2 text-xs text-emerald-700">{esito}</p>}

      <Dialog open={!!conferma} onOpenChange={(v) => !v && setConferma(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Unire le righe della NDP {conferma?.ndp_numero}?</DialogTitle>
            <DialogDescription>
              {conferma?.sospetto
                ? "Attenzione: nessuna di queste righe nasce da una NDP, quindi potrebbero essere due carichi diversi marcati con lo stesso numero. Guarda i dati qui sotto prima di unirli."
                : "Resta una riga sola. I dati del documento vengono dalla NDP più recente; ritiro, note e «pronto dal» restano quelli già inseriti."}
            </DialogDescription>
          </DialogHeader>
          <ul className="max-h-56 space-y-1 overflow-y-auto text-xs">
            {conferma?.righe.map((r) => (
              <li key={r.id} className="rounded-md border bg-muted/40 p-2">
                <span className="font-medium">{r.genesi}</span> · {STATO_LABEL[r.stato as StatoCaricoPrevisto] ?? r.stato} · creata{" "}
                {new Date(r.creato_il).toLocaleDateString("it-IT")}
                <span className="block text-muted-foreground">
                  {[
                    r.data_ritiro && `ritiro ${new Date(r.data_ritiro).toLocaleDateString("it-IT")}`,
                    r.pronto_dal &&
                      `pronto dal ${new Date(r.pronto_dal).toLocaleDateString("it-IT")}`,
                    r.pallet_previsti && `${r.pallet_previsti} pallet`,
                    r.note && `nota «${r.note}»`,
                  ]
                    .filter(Boolean)
                    .join(" · ") || "nessun dato oltre al documento"}
                </span>
              </li>
            ))}
          </ul>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setConferma(null)}
              className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted"
            >
              Annulla
            </button>
            <button
              type="button"
              disabled={fondi.isPending}
              onClick={() => {
                const g = conferma;
                if (!g) return;
                fondi.mutate(g.ndp_numero, {
                  onSuccess: (r) => {
                    setConferma(null);
                    const n = r.fusi[0]?.assorbite.length ?? 0;
                    const recuperati = r.fusi[0]?.campi_recuperati ?? [];
                    setEsito(
                      `NDP ${g.ndp_numero}: ${n === 1 ? "una riga assorbita" : `${n} righe assorbite`}` +
                        (recuperati.length ? `, recuperati ${recuperati.join(", ")}.` : "."),
                    );
                  },
                });
              }}
              className="rounded-md border bg-foreground px-3 py-1.5 text-sm font-medium text-background hover:opacity-90 disabled:opacity-50"
            >
              Unisci
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Le anomalie del tabellone (08-10-2026): righe che il gestionale smentisce,
 * rilevate dal giro dei 15 minuti. Nato dal caso Stone Alliance — partita il 31/08
 * con la NDP ancora aperta in Cube — rimasto invisibile per cinque settimane.
 *
 * Chiuso di default (una riga con il conteggio), sparisce quando non ce ne sono.
 * Ogni scheda dice cosa non torna, con i fatti, e cosa fare; dove la correzione è
 * sicura c'è il pulsante, che chiede sempre conferma. Lo vede chiunque veda il
 * tabellone: Marina corregge da sola.
 */
function RiquadroAnomalie({ onApri }: { onApri: (avvisoId: string) => void }) {
  const { data } = useAnomalieTabellone();
  const correggi = useCorreggiAnomalia();
  const ignora = useIgnoraAnomalia();
  const [aperto, setAperto] = useState(false);
  const [daCorreggere, setDaCorreggere] = useState<AnomaliaTabellone | null>(null);
  const [daIgnorare, setDaIgnorare] = useState<AnomaliaTabellone | null>(null);
  const [nota, setNota] = useState("");
  const [esito, setEsito] = useState<string | null>(null);
  const anomalie = data ?? [];

  if (!anomalie.length) {
    return esito ? (
      <p className="text-xs text-emerald-700" role="status" data-testid="anomalie-esito">
        {esito}
      </p>
    ) : null;
  }

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50/60" data-testid="anomalie">
      <button
        type="button"
        onClick={() => setAperto((v) => !v)}
        aria-expanded={aperto}
        className="flex w-full items-center gap-2 px-3 py-2 text-left"
        data-testid="anomalie-apri"
      >
        <AlertTriangle className="h-4 w-4 shrink-0 text-amber-700" />
        <span className="text-sm font-medium text-amber-900">
          {anomalie.length === 1
            ? "Un'anomalia da controllare"
            : `${anomalie.length} anomalie da controllare`}
        </span>
        <span className="hidden text-xs text-amber-800 sm:inline">
          · righe che il gestionale smentisce, controllate ogni 15 minuti
        </span>
        <span className="ml-auto flex items-center gap-1 text-xs font-medium text-amber-900">
          {aperto ? "Nascondi" : "Mostra"}
          {aperto ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </span>
      </button>
      {esito && <p className="px-3 pb-2 text-xs text-emerald-700">{esito}</p>}

      {aperto && (
        <ul className="space-y-2 border-t border-amber-200 p-3">
          {anomalie.map((x) => (
            <li
              key={x.id}
              className="rounded-md border border-amber-200 bg-card p-3 text-xs"
              data-testid="anomalia"
            >
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="text-sm font-medium text-foreground">{x.titolo}</span>
                <span className="text-muted-foreground">
                  · {x.cliente_nome} · rilevata il{" "}
                  {new Date(x.rilevata_il).toLocaleDateString("it-IT")}
                </span>
              </div>
              <p className="mt-1 text-foreground/90">{x.motivo}</p>
              <p className="mt-1 text-amber-900">
                <span className="font-medium">Cosa fare: </span>
                {x.suggerimento}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {x.azione && (
                  <button
                    type="button"
                    onClick={() => {
                      correggi.reset();
                      setDaCorreggere(x);
                    }}
                    className="rounded-md border bg-foreground px-2.5 py-1 font-medium text-background hover:opacity-90"
                    data-testid="anomalia-correggi"
                  >
                    {x.azione_etichetta}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => onApri(x.avviso_id)}
                  className="rounded-md border border-amber-400 bg-card px-2.5 py-1 font-medium hover:bg-amber-100"
                >
                  Apri la riga
                </button>
                <button
                  type="button"
                  onClick={() => {
                    ignora.reset();
                    setNota("");
                    setDaIgnorare(x);
                  }}
                  className="rounded-md px-2.5 py-1 text-muted-foreground hover:bg-muted"
                  data-testid="anomalia-ignora"
                >
                  Non è un'anomalia
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={!!daCorreggere} onOpenChange={(v) => !v && setDaCorreggere(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {daCorreggere?.azione_etichetta} · {daCorreggere?.cliente_nome}
            </DialogTitle>
            <DialogDescription>{daCorreggere?.suggerimento}</DialogDescription>
          </DialogHeader>
          <p className="rounded-md border bg-muted/40 p-2 text-xs">{daCorreggere?.motivo}</p>
          {correggi.isError && (
            <p className="text-xs text-red-700" role="alert">
              {(correggi.error as Error).message}
            </p>
          )}
          <DialogFooter>
            <button
              type="button"
              onClick={() => setDaCorreggere(null)}
              className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted"
            >
              Annulla
            </button>
            <button
              type="button"
              disabled={correggi.isPending}
              onClick={() => {
                const x = daCorreggere;
                if (!x) return;
                correggi.mutate(x.id, {
                  onSuccess: (r) => {
                    setDaCorreggere(null);
                    setEsito(
                      x.azione === "riporta"
                        ? `${x.cliente_nome}: di nuovo sul tabellone` +
                            (r.unita ? `, unita alla riga «${r.unita.cliente}».` : ".")
                        : x.azione === "unisci"
                          ? `${x.cliente_nome}: una riga sola, quella della NDP.`
                          : `${x.cliente_nome}: riga archiviata.`,
                    );
                  },
                });
              }}
              className="rounded-md border bg-foreground px-3 py-1.5 text-sm font-medium text-background hover:opacity-90 disabled:opacity-50"
              data-testid="anomalia-conferma"
            >
              {correggi.isPending ? "Un momento…" : "Conferma"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!daIgnorare} onOpenChange={(v) => !v && setDaIgnorare(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Non è un'anomalia?</DialogTitle>
            <DialogDescription>
              La segnalazione si chiude e non torna, anche se la riga resta così. Se puoi,
              scrivi perché: serve a migliorare il controllo.
            </DialogDescription>
          </DialogHeader>
          <textarea
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            rows={3}
            placeholder="Per esempio: la merce è partita, il DDT è stato fatto a mano"
            className="w-full rounded-md border bg-card p-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          {ignora.isError && (
            <p className="text-xs text-red-700" role="alert">
              {(ignora.error as Error).message}
            </p>
          )}
          <DialogFooter>
            <button
              type="button"
              onClick={() => setDaIgnorare(null)}
              className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted"
            >
              Annulla
            </button>
            <button
              type="button"
              disabled={ignora.isPending}
              onClick={() => {
                const x = daIgnorare;
                if (!x) return;
                ignora.mutate(
                  { id: x.id, nota },
                  {
                    onSuccess: () => {
                      setDaIgnorare(null);
                      setEsito(`${x.cliente_nome}: segnalazione chiusa.`);
                    },
                  },
                );
              }}
              className="rounded-md border bg-foreground px-3 py-1.5 text-sm font-medium text-background hover:opacity-90 disabled:opacity-50"
              data-testid="anomalia-ignora-conferma"
            >
              Chiudi la segnalazione
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Il nome di chi ritira letto da Aida, se non è un segnaposto. */
const letto = (r: CaricoPrevistoRow) =>
  r.trasportatore_letto && r.trasportatore_letto !== "<UNKNOWN>" ? r.trasportatore_letto : null;

export function CarichiPrevistiView({
  onApriChat,
}: {
  /** Apre la sidebar di Aida sulla domanda della riga. Senza, la frase «rispondi
   *  dalla chat» sarebbe un vicolo cieco: era il caso (5) trovato alla prova. */
  onApriChat?: (avvio: AvvioChat) => void;
} = {}) {
  const [filtro, setFiltro] = useState<Filtro>("attivi");
  const [ricerca, setRicerca] = useState("");
  const [ord, setOrd] = useState<Ordine>(null);
  const [selId, setSelId] = useState<string | null>(null);
  // L'ultimo carico eliminato dal drawer: il messaggio resta finché Marina non
  // lo chiude, con «Annulla» che lo rimette sul tabellone (01-10-2026).
  const [eliminato, setEliminato] = useState<CaricoEliminato | null>(null);
  const ripristina = useCpRipristina();
  // Il pacchetto documenti aperto sopra la riga (null = chiuso).
  const [plicoId, setPlicoId] = useState<string | null>(null);
  const [nuovo, setNuovo] = useState(false);
  const [, setSearchParams] = useSearchParams();

  // La ricerca AZZERA il filtro e cerca su tutto il tabellone, uscite comprese:
  // un meccanismo attivo alla volta, e nessun «non lo trovo» perché la riga era
  // filtrata altrove.
  const cercando = ricerca.trim().length > 0;
  const { data, isLoading } = useCarichiPrevisti(cercando ? "tutti" : filtro);
  const conteggi = data?.conteggi;

  const items = ordina(cercaRighe(data?.items ?? [], ricerca), ord);

  function cambiaOrd(col: Colonna) {
    // Terzo click: si torna all'ordine di immissione, che è il default.
    setOrd((o) => (o?.col !== col ? { col, desc: false } : o.desc ? null : { col, desc: true }));
  }

  return (
    <div className="space-y-4">
      {/* Sopra ogni cosa: le NDP che compaiono su più righe. Sparisce da sé
          quando non ce ne sono più. */}
      <RiquadroDuplicati />

      {/* La testata: cinque riquadri che sono insieme il numero e il filtro. */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
        {RIQUADRI.map((r) => (
          <Riquadro
            key={r.id}
            icona={r.icona}
            label={r.label}
            valore={conteggi?.[r.id]}
            urgente={r.urgente}
            // Mentre si cerca nessun riquadro è acceso: il filtro non è più in gioco.
            attivo={!cercando && filtro === r.id}
            onClick={() => {
              setRicerca("");
              setFiltro(r.id);
            }}
          />
        ))}
      </div>

      {/* Ricerca + rilascio documenti: la seconda e ultima fascia. */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={ricerca}
            onChange={(e) => setRicerca(e.target.value)}
            placeholder="Cerca cliente, commessa, NDP, riferimento del cliente, targa o note…"
            className={cn(
              "h-[38px] w-full rounded-md border bg-card pl-8 pr-8 text-sm",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            )}
          />
          {cercando && (
            <button
              type="button"
              onClick={() => setRicerca("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:bg-muted"
              aria-label="Pulisci la ricerca"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        {/* «+ Nuovo carico» sta accanto alla ricerca, non sopra la tabella: è
            l'azione della fascia dove si lavora sulle righe, e non compete con
            i cinque riquadri-filtro della fascia di sopra. */}
        <Button
          variant="outline"
          onClick={() => setNuovo(true)}
          className="h-[38px] shrink-0"
          title="Aggiungi a mano una riga al tabellone"
        >
          <Plus className="mr-1.5 h-4 w-4" />
          Nuovo carico
        </Button>
        <RiquadroRilascio
          onRiga={(caricoId) => {
            const riga = (data?.items ?? []).find((r) => r.carico_id === caricoId);
            if (riga) setSelId(riga.id);
          }}
          onDocumenti={() =>
            setSearchParams((p) => {
              const next = new URLSearchParams(p);
              next.set("tab", "documenti");
              return next;
            })
          }
        />
      </div>

      {/* Subito sotto la ricerca: le righe che il gestionale smentisce. */}
      <RiquadroAnomalie onApri={setSelId} />

      {/* In partenza oggi: la priorità del giorno, sopra tutto il resto.
          Sostituisce il foglio che Marina stampava per il presidente. */}
      <BandaPartenze items={items} onApri={setSelId} />

      {eliminato && (
        <div
          className="flex items-center gap-2 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs text-emerald-950"
          role="status"
          data-testid="carico-eliminato"
        >
          <Check className="h-3.5 w-3.5 shrink-0 text-emerald-700" />
          <span className="flex-1">
            {ripristina.isSuccess
              ? `Il carico di ${eliminato.cliente_nome} è tornato sul tabellone.`
              : eliminato.fatto.map((f) => f.charAt(0).toUpperCase() + f.slice(1)).join(" · ")}
          </span>
          {!ripristina.isSuccess && (
            <button
              type="button"
              disabled={ripristina.isPending}
              onClick={() => ripristina.mutate(eliminato)}
              className="rounded border border-emerald-400 bg-white px-2 py-0.5 font-medium hover:bg-emerald-100 disabled:opacity-50"
            >
              Annulla
            </button>
          )}
          {ripristina.isError && (
            <span className="text-red-700">{(ripristina.error as Error).message}</span>
          )}
          <button
            type="button"
            onClick={() => {
              setEliminato(null);
              ripristina.reset();
            }}
            className="rounded p-0.5 hover:bg-emerald-100"
            aria-label="Chiudi il messaggio"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      )}

      {/* Tabella */}
      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : items.length === 0 ? (
        <p className="py-12 text-center text-sm text-muted-foreground">
          {cercando
            ? `Nessuna riga per «${ricerca.trim()}» — la ricerca guarda cliente, commessa, NDP, riferimenti del cliente e note.`
            : "Nessun carico previsto in questa coda."}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/40 text-left text-xs text-muted-foreground">
              {/* Undici colonne invece di tredici: «Avvisato il» è rientrato in
                  «Tempo», i riferimenti sotto la commessa. */}
              {/* Ordinabili solo le colonne dati: Attenzione, Uscita e Note non
                  hanno un ordine sensato e restano intestazioni normali. */}
              <tr>
                <th className="px-2.5 py-2 font-medium" title="Cosa richiede la tua attenzione">
                  <span className="sr-only">Attenzione</span>
                  <HelpCircle className="h-3.5 w-3.5" />
                </th>
                <IntestazioneOrd col="cliente" label="Cliente" ord={ord} onOrd={cambiaOrd} />
                <IntestazioneOrd col="commessa" label="Commessa" ord={ord} onOrd={cambiaOrd} />
                <IntestazioneOrd col="stato" label="Stato" ord={ord} onOrd={cambiaOrd} />
                <IntestazioneOrd
                  col="data_ritiro" label="Ritiro" ord={ord} onOrd={cambiaOrd} className="bg-brand-50"
                />
                <IntestazioneOrd
                  col="pronto_dal" label="Pronto dal" ord={ord} onOrd={cambiaOrd} className="bg-brand-50"
                />
                <IntestazioneOrd
                  col="pallet" label="Pallet" ord={ord} onOrd={cambiaOrd} className="bg-brand-50"
                />
                <IntestazioneOrd col="ndp" label="NDP" ord={ord} onOrd={cambiaOrd} />
                <IntestazioneOrd col="ddt" label="DDT" ord={ord} onOrd={cambiaOrd} />
                <th className="px-2.5 py-2 font-medium">Uscita</th>
                <IntestazioneOrd col="tempo" label="Tempo" ord={ord} onOrd={cambiaOrd} />
                <th className="px-2.5 py-2 font-medium">Note</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <Riga
                  key={row.id}
                  row={row}
                  soglia={data?.soglia_attesa_spedizione_giorni ?? 7}
                  onClick={() => setSelId(row.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {nuovo && <NuovoCaricoDialog onClose={() => setNuovo(false)} />}

      {selId && (
        <CaricoPrevistoDrawer
          id={selId}
          onClose={() => setSelId(null)}
          // Il drawer copre il dock di Aida e occupa lo stesso bordo destro: non
          // sono due pannelli da tenere aperti insieme, sono due modi di lavorare
          // sulla stessa riga. Quindi si chiude, e il contesto passa nella
          // conversazione — che è il motivo per cui Aida ripete la domanda.
          onApriChat={(avvio) => {
            setSelId(null);
            onApriChat?.(avvio);
          }}
          // Il pacchetto documenti si apre SOPRA la riga: chiudendolo si torna
          // dov'eri, invece di essere sbalzato in un'altra sezione.
          onApriPlico={(caricoId) => setPlicoId(caricoId)}
          onEliminato={(e) => {
            setSelId(null);
            ripristina.reset();
            setEliminato(e);
          }}
        />
      )}

      {plicoId && (
        <PacchettoDrawer
          caricoId={plicoId}
          onClose={() => setPlicoId(null)}
          etichettaIndietro="Torna al carico"
        />
      )}
    </div>
  );
}

/** Le righe segnate in partenza oggi, in cima e ben visibili. */
function BandaPartenze({
  items,
  onApri,
}: {
  items: CaricoPrevistoRow[];
  onApri: (id: string) => void;
}) {
  const oggi = new Date().toISOString().slice(0, 10);
  const oggiInPartenza = items.filter((r) => r.data_partenza_prevista === oggi);
  if (oggiInPartenza.length === 0) return null;

  const pallet = oggiInPartenza.reduce((s, r) => s + (r.pallet_previsti ?? 0), 0);
  return (
    <div className="rounded-lg border border-brand-200 bg-brand-50/60 p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-brand-700">
          <Truck className="h-4 w-4" />
          In partenza oggi ({oggiInPartenza.length})
        </h3>
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span>{Math.round(pallet)} pallet</span>
          <Link to="/partenze" className="font-medium text-brand-700 hover:underline">
            Tabellone partenze →
          </Link>
        </div>
      </div>
      <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {oggiInPartenza.map((r) => (
          <li key={r.id}>
            <button
              type="button"
              onClick={() => onApri(r.id)}
              className="w-full rounded-md border bg-card px-3 py-2 text-left hover:bg-muted"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-sm font-medium">{r.cliente_nome}</span>
                {r.partito_il ? (
                  <span className="shrink-0 text-[11px] font-medium text-emerald-700">uscita</span>
                ) : r.ddt_numero ? (
                  // Il DDT di oggi è già il segnale che esce: niente da fare per Marina.
                  <span className="shrink-0 text-[11px] font-medium text-amber-700">DDT {r.ddt_numero}</span>
                ) : (
                  <span className="shrink-0 text-[11px] font-medium text-amber-700">da far uscire</span>
                )}
              </div>
              <div className="mt-0.5 text-[11px] text-muted-foreground">
                {r.commesse.length > 0 ? `commessa ${r.commesse.join(", ")}` : "—"}
                {r.pallet_previsti != null && ` · ${Math.round(r.pallet_previsti)} pallet`}
              </div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
